import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import { NovalaLogo } from '../../components/NovalaLogo';
import { CheckThick } from '../../components/icons';
import { BUSINESS_TYPES, INDUSTRIES, TEAM_SIZE_IDS } from './constants';
import type { Step } from './types';
import { useCurrentUser } from './hooks/useCurrentUser';
import { BizRow } from './components/BizRow';
import { SubItem } from './components/SubItem';
import { Segmented } from './components/Segmented';
import { IndustryDropdown } from './components/IndustryDropdown';
import { useTranslation } from 'react-i18next';

export default function Onboarding(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();

  const [step, setStep] = useState<Step>(1);
  const [companyName, setCompanyName] = useState('');
  const [bizType, setBizType] = useState('');
  const [industry, setIndustry] = useState('');
  const [subIndustry, setSubIndustry] = useState('');
  const [otherBizType, setOtherBizType] = useState('');
  const [otherIndustry, setOtherIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErr1, setShowErr1] = useState(false);
  const [showErr2, setShowErr2] = useState(false);
  const [animateSuccess, setAnimateSuccess] = useState(false);

  useEffect(() => {
    if (step === 3) {
      setAnimateSuccess(false);
      const raf = requestAnimationFrame(() => setAnimateSuccess(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [step]);

  const canContinue1 = companyName.trim().length >= 2 && bizType !== '' && (bizType !== 'other' || otherBizType.trim().length >= 2);
  const currentIndustry = INDUSTRIES.find((i) => i.id === industry);
  const hasSubs = !!(currentIndustry && currentIndustry.subIds && currentIndustry.subIds.length > 0);
  const canContinue2 =
    industry !== '' &&
    (!hasSubs || subIndustry !== '') &&
    (industry !== 'other' || otherIndustry.trim().length >= 2) &&
    teamSize !== '';

  const industryLabel = currentIndustry ? t('onboarding.industries.' + currentIndustry.id + '.label') : '';
  const subIndustryLabel = subIndustry && currentIndustry ? t('onboarding.industries.' + currentIndustry.id + '.subs.' + subIndustry) : '';
  const industryDisplay =
    industry === 'other'
      ? (otherIndustry.trim() || t('onboarding.industries.other.label'))
      : currentIndustry
        ? (subIndustry ? industryLabel + ' · ' + subIndustryLabel : industryLabel)
        : '';
  const bizLabel = bizType === 'other' ? (otherBizType.trim() || t('onboarding.businessTypes.other.label')) : (bizType ? t('onboarding.businessTypes.' + bizType + '.label') : '');
  const teamLabel = teamSize ? t('onboarding.teamSizes.' + teamSize) : '';
  const firstName = (user.fullName || '').split(' ')[0] || user.firstName || t('onboarding.fallbackFirstName');

  async function submit(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      // TODO(claire): confirm the endpoint and auth headers used elsewhere in the app.
      const res = await fetch('/onboarding/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: companyName.trim(),
          businessType: bizType,
          otherBusinessType: bizType === 'other' ? otherBizType.trim() : '',
          industry,
          subIndustry: hasSubs ? subIndustry : '',
          otherIndustry: industry === 'other' ? otherIndustry.trim() : '',
          teamSize,
        }),
      });
      if (!res.ok) throw new Error(t('onboarding.errSaveFailed'));
      setStep(3);
    } catch (e: any) {
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        console.warn('Local dev: backend unreachable, advancing to Screen 3 for UI testing');
        setStep(3);
        return;
      }
      setError((e && e.message) || t('onboarding.errGeneric'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-page flex flex-col items-center justify-start px-4 py-10">
      <div className="w-full max-w-[560px]">

        {step === 1 && (
          <div>
            <div className="flex flex-col items-center mb-8">
              <NovalaLogo />
              <h1 className="text-[22px] font-medium text-ink text-center mt-5">
                {t('onboarding.welcome')}
              </h1>
              <p className="text-[14px] text-[#334155] text-center mt-1.5">
                {t('onboarding.welcomeSubtitle')}
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-hairline p-8 sm:p-10">
              <div>
                <Label htmlFor="companyName" className="text-[14px] font-medium text-ink">
                  {t('onboarding.companyLabel')}
                </Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t('onboarding.companyPlaceholder')}
                  className="mt-2"
                  autoFocus
                />
              </div>
              <div className="mt-6">
                <div className="text-[14px] font-medium text-ink mb-2">
                  {t('onboarding.businessTypeQuestion')}
                </div>
                {BUSINESS_TYPES.map((bt) => (
                  <BizRow
                    key={bt.id}
                    selected={bizType === bt.id}
                    onClick={() => setBizType(bt.id)}
                    label={t('onboarding.businessTypes.' + bt.id + '.label')}
                    desc={t('onboarding.businessTypes.' + bt.id + '.desc')}
                  />
                ))}
                {bizType === 'other' && (
                  <div className="mt-3 border-l-[3px] border-brand pl-3.5">
                    <Label htmlFor="otherBizType" className="text-[12px] font-medium text-ink">
                      {t('onboarding.otherBusinessLabel')}
                    </Label>
                    <Input
                      id="otherBizType"
                      value={otherBizType}
                      onChange={(e) => setOtherBizType(e.target.value)}
                      placeholder={t('onboarding.otherBusinessPlaceholder')}
                      className="mt-2"
                      autoFocus
                    />
                  </div>
                )}
              </div>
              {showErr1 && !canContinue1 && (
                <div className="text-[13px] text-danger mt-4 space-y-1">
                  {companyName.trim().length < 2 && <div>{t('onboarding.errCompanyName')}</div>}
                  {bizType === '' && <div>{t('onboarding.errBusinessType')}</div>}
                  {bizType === 'other' && otherBizType.trim().length < 2 && <div>{t('onboarding.errOtherBusinessType')}</div>}
                </div>
              )}
              <Button
                onClick={() => {
                  if (!canContinue1) { setShowErr1(true); return; }
                  setShowErr1(false);
                  setStep(2);
                }}
                className="w-full mt-6"
              >
                {t('onboarding.continueBtn')}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="bg-white rounded-2xl border border-hairline p-8 sm:p-10">
              <div className="flex gap-1.5 mb-1.5">
                <div className="h-1 flex-1 rounded-full bg-brand" />
                <div className="h-1 flex-1 rounded-full bg-brand" />
                <div className="h-1 flex-1 rounded-full bg-stroke" />
              </div>
              <div className="text-[12px] font-medium text-[#334155] mb-6">{t('onboarding.step2Progress')}</div>
              <h2 className="text-[22px] font-medium text-ink">{t('onboarding.step2Title')}</h2>
              <p className="text-[14px] text-[#334155] mt-1.5 mb-6">
                {t('onboarding.step2Subtitle')}
              </p>

              <div>
                <div className="text-[14px] font-medium text-ink mb-2">{t('onboarding.industryQuestion')}</div>
                <IndustryDropdown
                  value={industry}
                  onChange={(id) => {
                    setIndustry(id);
                    setSubIndustry('');
                  }}
                />
                {industry === 'other' && (
                  <div className="mt-3 border-l-[3px] border-brand pl-3.5">
                    <Label htmlFor="otherIndustry" className="text-[12px] font-medium text-ink">
                      {t('onboarding.otherIndustryLabel')}
                    </Label>
                    <Input
                      id="otherIndustry"
                      value={otherIndustry}
                      onChange={(e) => setOtherIndustry(e.target.value)}
                      placeholder={t('onboarding.otherIndustryPlaceholder')}
                      className="mt-2"
                      autoFocus
                    />
                  </div>
                )}
                {hasSubs && currentIndustry && (
                  <div className="mt-3 border-l-[3px] border-brand pl-3.5">
                    <div className="text-[12px] font-medium text-ink mb-2.5">
                      {t('onboarding.subIndustryQuestion', { industry: t('onboarding.industries.' + currentIndustry.id + '.label').toLowerCase() })}
                    </div>
                    {currentIndustry.subIds!.map((subId) => (
                      <SubItem
                        key={subId}
                        selected={subIndustry === subId}
                        onClick={() => setSubIndustry(subId)}
                        label={t('onboarding.industries.' + currentIndustry.id + '.subs.' + subId)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6">
                <div className="text-[14px] font-medium text-ink mb-2">
                  {t('onboarding.teamQuestion')}
                </div>
                <Segmented options={TEAM_SIZE_IDS.map((id) => ({ id, label: t('onboarding.teamSizes.' + id) }))} value={teamSize} onChange={setTeamSize} />
              </div>

              {showErr2 && !canContinue2 && (
                <div className="text-[13px] text-danger mt-4 space-y-1">
                  {industry === '' && <div>{t('onboarding.errIndustry')}</div>}
                  {industry === 'other' && otherIndustry.trim().length < 2 && <div>{t('onboarding.errOtherIndustry')}</div>}
                  {hasSubs && subIndustry === '' && currentIndustry && <div>{t('onboarding.errSubIndustry', { industry: t('onboarding.industries.' + currentIndustry.id + '.label').toLowerCase() })}</div>}
                  {teamSize === '' && <div>{t('onboarding.errTeamSize')}</div>}
                </div>
              )}
              {error && <div className="text-[13px] text-danger mt-4">{error}</div>}

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={saving}
                  className="flex-1 bg-white border-2 border-ink text-ink text-[15px] font-medium py-3 px-4 rounded-lg hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
                >
                  {t('onboarding.back')}
                </button>
                <Button
                  onClick={() => {
                    if (!canContinue2) { setShowErr2(true); return; }
                    setShowErr2(false);
                    submit();
                  }}
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? t('onboarding.saving') : t('onboarding.continueBtn')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white rounded-2xl border border-hairline p-8 sm:p-10">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'transition-all duration-500 ease-out',
                  animateSuccess ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                )}
                style={{ transitionDelay: '0ms' }}
              >
                <NovalaLogo />
              </div>
              <div
                className={cn(
                  'w-[84px] h-[84px] rounded-full bg-brand flex items-center justify-center mt-6 mb-6',
                  'transition-all duration-500 ease-out',
                  animateSuccess ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                )}
                style={{ transitionDelay: '200ms' }}
              >
                <CheckThick className="w-[46px] h-[46px] text-white" />
              </div>
              <h1
                className={cn(
                  'text-[26px] font-medium text-ink text-center',
                  'transition-all duration-500 ease-out',
                  animateSuccess ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: '450ms' }}
              >
                {t('onboarding.successTitle', { firstName })}
              </h1>
              <p
                className={cn(
                  'text-[15px] text-[#334155] text-center mt-2.5 max-w-[380px]',
                  'transition-all duration-500 ease-out',
                  animateSuccess ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: '650ms' }}
              >
                {t('onboarding.successSubtitle', { companyName: companyName.trim(), industry: industryDisplay.toLowerCase(), business: bizLabel.toLowerCase() })}
              </p>
              <div
                className={cn(
                  'w-full bg-white rounded-xl border border-hairline p-5 mt-6',
                  'transition-all duration-500 ease-out',
                  animateSuccess ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: '850ms' }}
              >
                <div className="text-[11px] font-medium text-[#334155] uppercase tracking-wider mb-3.5">
                  {t('onboarding.workspaceTitle')}
                </div>
                <div className="py-3">
                  <div className="text-[12px] font-medium text-[#334155] uppercase tracking-wide mb-1">{t('onboarding.companyDetailLabel')}</div>
                  <div className="text-[15px] font-medium text-ink">{companyName.trim()}</div>
                </div>
                <div className="py-3 border-t border-stroke">
                  <div className="text-[12px] font-medium text-[#334155] uppercase tracking-wide mb-1">{t('onboarding.industryDetailLabel')}</div>
                  <div className="text-[15px] font-medium text-ink">{industryDisplay}</div>
                </div>
                <div className="py-3 border-t border-stroke">
                  <div className="text-[12px] font-medium text-[#334155] uppercase tracking-wide mb-1">{t('onboarding.structureDetailLabel')}</div>
                  <div className="text-[15px] font-medium text-ink">{bizLabel}</div>
                </div>
                <div className="py-3 border-t border-stroke">
                  <div className="text-[12px] font-medium text-[#334155] uppercase tracking-wide mb-1">{t('onboarding.teamSizeDetailLabel')}</div>
                  <div className="text-[15px] font-medium text-ink">{teamLabel}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className={cn(
                  'w-full mt-6 py-3 px-4 text-[15px] font-medium text-white bg-[#0E1A1A] hover:bg-black rounded-lg transition-colors',
                  animateSuccess ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
                  'transition-all duration-500 ease-out'
                )}
                style={{ transitionDelay: '1050ms' }}
              >
                {t('onboarding.goToLogin')}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
